import { Headphones, Mic, Music } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import type { PlannedSoundtrack, UiLang } from "../lib/types";

interface Props {
  soundtrack: PlannedSoundtrack[];
  /** Kjøretid for hele ruta. Sier hvor mye man trenger å ha med. */
  travelHours: number;
  lang: UiLang;
}

/**
 * Noe å høre på langs ruta.
 *
 * Kjøretiden står øverst fordi det er den som avgjør hvor mye man trenger: seks
 * timer er to album og en podkast, halvannen er ett album. Uten tallet er dette
 * en liste med musikk, og med det er det et svar på hvor lang etappen er.
 *
 * Lenkene er søk, ikke spillelister. Se api/src/data/soundtrack.ts for hvorfor:
 * kort sagt at appen ikke skal be om tilgang til noens musikkonto for å foreslå
 * Grieg, og at en oppdiktet spilleliste-id er verre enn ingen lenke fordi den
 * ser ut til å virke.
 */
export function SoundtrackPanel({ soundtrack, travelHours, lang }: Props) {
  const t = dictionaries[lang];
  if (soundtrack.length === 0) return null;

  return (
    <div className="card soundtrack">
      <div className="card-title">
        <Headphones size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
        {t.soundtrackTitle}
      </div>
      <p className="card-sub">{t.soundtrackSubtitle(travelHours)}</p>

      <ul className="soundtrack-list">
        {soundtrack.map((entry) => (
          <li className="soundtrack-item" key={`${entry.artist}-${entry.title ?? ""}`}>
            <div className="soundtrack-head">
              <span className="soundtrack-icon">
                {entry.kind === "podcast" ? (
                  <Mic size={12} strokeWidth={2.2} />
                ) : (
                  <Music size={12} strokeWidth={2.2} />
                )}
              </span>
              <strong className="soundtrack-artist">{entry.artist}</strong>
              {/* Tittelen mangler der utvalget hviler på artisten selv. */}
              {entry.title && <span className="soundtrack-work">{entry.title}</span>}
              {entry.kind === "podcast" && (
                <span className="soundtrack-kind">{t.soundtrackPodcast}</span>
              )}
            </div>

            <p className="soundtrack-note">{entry.note}</p>

            <div className="soundtrack-links">
              <a href={entry.spotify} target="_blank" rel="noreferrer">
                Spotify
              </a>
              <a href={entry.appleMusic} target="_blank" rel="noreferrer">
                Apple Music
              </a>
            </div>
          </li>
        ))}
      </ul>

      <p className="soundtrack-note soundtrack-source">{t.soundtrackSearchNote}</p>
    </div>
  );
}
